import { v7 as uuid } from 'uuid'
import { ReviewStatus } from '@skop-app/lib-types'
import { getMetadataValue } from '@/features/property/options/metadata'
import {
  BUSINESS_LANGUAGES,
  LEGACY_BASE_LANGUAGE,
} from '@/utils/languagePolicy'
import type {
  FieldItem,
  Metadata,
  OptionCreateInput,
  OptionItem,
  OptionRow,
  SupportedKeys,
  WithGroupRecursive,
} from '@/services/property'
import type { Language, TranslationInput, TranslationSchema } from '@/types'
import { hasDefaultOptionFlag } from './option.defaults'

// =============================================================================
// Normalized Option Types
// =============================================================================
//
// These types are used for cache storage and view transformation:
// - NormalizedOption: Flat representation for normalized cache (by ID)
// - ViewOption: Hierarchical representation for UI display (translations applied)
//
// After transformation, these become OptionRow (from property.type.ts) for AG Grid.
// =============================================================================

/**
 * Normalized option stored in cache (optionsById).
 * Includes translations indexed by language code.
 */
export type NormalizedOption = {
  id: string
  label: string
  value?: string
  isDefault?: boolean
  status?: string
  updatedAt?: string
  fieldOptionsId?: string | null
  optionOptionsId?: string | null
  metadata: Metadata[]
  userID?: string
  organizationID?: string
} & Partial<Record<Language, TranslationSchema>>

export type NormalizedFieldOptions = {
  fieldId: string
  fieldKey: string
  optionsById: Record<string, NormalizedOption>
}

/**
 * Minimal option type ready for react-select consumption.
 * Contains only the essential fields needed for select components.
 */
export type SelectOption = {
  id: string
  label: string
  value: string
  isInReview: boolean
  isDefault?: boolean
  status?: string
  isSelectable?: boolean
  icon?: string
  isQuickAction?: boolean
  userID?: string
  organizationID?: string
  metadata?: Metadata[]
  options?: SelectOption[]
}

export type FieldOptionsViewData = {
  options: ViewOption[]
  flatOptions: ViewOption[]
  optionsById: Record<string, NormalizedOption>
  /**
   * AG Grid `refData` mapping (id -> label) for these options (localized to `lang`).
   * Useful when grid stores only option IDs but needs fast label rendering/filtering.
   */
  refDataById: Record<string, string>
  supportedKey: SupportedKeys
  /**
   * Options transformed for react-select consumption.
   * Contains minimal fields:
   * id, label, value, isInReview, icon, isQuickAction, userID, options (nested).
   */
  selectOptions: SelectOption[]
}

/**
 * Option for UI display (translations applied, hierarchical).
 * This type is used before flattening into OptionRow for AG Grid.
 */
export type ViewOption = {
  id: string
  label: string
  value?: string | null
  isDefault?: boolean
  status?: string | null
  updatedAt?: string | null
  fieldOptionsId?: string | null
  optionOptionsId?: string | null
  metadata: Metadata[]
  userID?: string | null
  organizationID?: string | null
  options?: ViewOption[]
} & Partial<Record<Language, TranslationSchema>>

export const normalizeOptionLabelForDiagnostics = (value: string | undefined) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const indexTranslationsByLang = (
  translations: TranslationSchema[] | undefined,
): Partial<Record<Language, TranslationSchema>> => {
  if (!translations || translations.length === 0) {
    return {}
  }

  return translations.reduce<Partial<Record<Language, TranslationSchema>>>(
    (acc, t) => {
      const lang =
        typeof t.lang === 'string' ? (t.lang.toUpperCase() as Language) : t.lang
      acc[lang] = {
        ...t,
        lang,
      }
      return acc
    },
    {},
  )
}

const appendUniqueChildId = (
  childrenByParent: Map<string, string[]>,
  parentId: string | undefined,
  childId: string,
) => {
  if (!parentId || parentId === childId) return

  const existingChildren = childrenByParent.get(parentId)
  if (!existingChildren) {
    childrenByParent.set(parentId, [childId])
    return
  }

  if (!existingChildren.includes(childId)) {
    existingChildren.push(childId)
  }
}

const mergeDefinedOptionFields = (
  current: OptionItem | undefined,
  next: Partial<OptionItem>,
): OptionItem => {
  const merged = { ...(current ?? {}) } as Record<string, unknown>

  for (const [key, value] of Object.entries(next)) {
    if (value !== undefined) {
      merged[key] = value
    }
  }

  return merged as OptionItem
}

export function canonicalizeOptionItems(items: OptionItem[]): OptionItem[] {
  const optionsById = new Map<string, OptionItem>()
  const parentById = new Map<string, string | undefined>()
  const explicitParentById = new Map<string, boolean>()

  const visit = (option: OptionItem, nestedParentId?: string) => {
    if (option.status === 'DELETED') {
      for (const child of option.options?.items ?? [])
        visit(child, nestedParentId)
      return
    }
    const { options, ...rest } = option
    const current = optionsById.get(option.id)
    const explicitParentId = option.optionOptionsId
    const currentHasExplicitParent = explicitParentById.get(option.id) === true
    const nextHasExplicitParent = Boolean(explicitParentId)
    const shouldUpdateParent =
      nextHasExplicitParent ||
      !current ||
      (!currentHasExplicitParent &&
        parentById.get(option.id) === undefined &&
        nestedParentId !== undefined)

    optionsById.set(option.id, mergeDefinedOptionFields(current, rest))

    if (shouldUpdateParent) {
      parentById.set(option.id, explicitParentId ?? nestedParentId)
      explicitParentById.set(option.id, nextHasExplicitParent)
    }

    for (const child of options?.items ?? []) {
      visit(child, option.id)
    }
  }

  for (const option of items) {
    visit(option)
  }

  const childrenByParent = new Map<string, string[]>()

  for (const [id] of optionsById) {
    const parentId = parentById.get(id)
    if (parentId && optionsById.has(parentId)) {
      appendUniqueChildId(childrenByParent, parentId, id)
    }
  }

  const buildNode = (
    id: string,
    parentId: string | null,
    lineage: Set<string> = new Set(),
  ): OptionItem | null => {
    if (lineage.has(id)) return null

    const option = optionsById.get(id)
    if (!option) return null

    const nextLineage = new Set(lineage)
    nextLineage.add(id)

    const children =
      childrenByParent
        .get(id)
        ?.map((childId) => buildNode(childId, id, nextLineage))
        .filter((child): child is OptionItem => child !== null) ?? []

    const {
      optionOptionsId: _ignoredParentId,
      options: _ignoredOptions,
      ...base
    } = option

    const withParent =
      parentId === null ? base : { ...base, optionOptionsId: parentId }

    return children.length > 0
      ? {
          ...withParent,
          options: {
            items: children,
          },
        }
      : withParent
  }

  const rootIds = Array.from(optionsById.keys()).filter((id) => {
    const parentId = parentById.get(id)
    return !parentId || !optionsById.has(parentId)
  })

  const result: OptionItem[] = []
  const visited = new Set<string>()

  const appendRoot = (id: string) => {
    if (visited.has(id)) return
    const node = buildNode(id, null)
    if (!node) return

    const markVisited = (current: OptionItem) => {
      visited.add(current.id)
      for (const child of current.options?.items ?? []) {
        markVisited(child)
      }
    }

    markVisited(node)
    result.push(node)
  }

  for (const rootId of rootIds) {
    appendRoot(rootId)
  }

  for (const id of optionsById.keys()) {
    appendRoot(id)
  }

  return result
}

export function normalizeFieldOptionsFromFieldItem(
  field: FieldItem,
): NormalizedFieldOptions {
  const fieldId = field.id
  const fieldKey = field.key

  const optionsById: Record<string, NormalizedOption> = {}

  const stack: { option: OptionItem; parentId: string | null }[] = []

  // racines
  for (const opt of canonicalizeOptionItems(field.options?.items ?? [])) {
    stack.push({ option: opt, parentId: null })
  }

  while (stack.length) {
    const { option, parentId } = stack.pop() as {
      option: OptionItem
      parentId: string | null
    }

    const id = option.id
    const isRoot = parentId === null

    const normalized: NormalizedOption = {
      id,
      label: option.label,
      value: option.value,
      isDefault: option.isDefault === true ? true : undefined,
      status: option.status,
      fieldOptionsId: isRoot
        ? (option.fieldOptionsId ?? fieldId)
        : (option.fieldOptionsId ?? null),
      optionOptionsId: isRoot
        ? (option.optionOptionsId ?? null)
        : (option.optionOptionsId ?? parentId),
      metadata: option.metadata ?? [],
      userID: option.userID,
      organizationID: option.organizationID ?? undefined,
      updatedAt: option.updatedAt,
      ...indexTranslationsByLang(option.translations?.items ?? []),
    }

    optionsById[id] = normalized

    for (const child of option.options?.items ?? []) {
      stack.push({ option: child, parentId: id })
    }
  }

  return { fieldId, fieldKey, optionsById }
}

// Traduction d’un label (field ou option) dans la langue cible
export function pickLabel(
  baseLabel: string | undefined,
  translations: TranslationSchema[] | undefined,
  lang: Language | undefined,
): string {
  if (!translations || translations.length === 0 || !lang) {
    return baseLabel ?? ''
  }
  const match = translations.find(
    (t) => typeof t.lang === 'string' && t.lang.toUpperCase() === lang,
  )
  if (match) return match.label
  return baseLabel ?? translations[0]?.label ?? ''
}

// Normalisation d’un FieldItem → NormalizedFieldOptions
export function buildViewTreeFromNormalized(
  normalized: NormalizedFieldOptions | undefined,
  lang: Language | undefined,
): ViewOption[] {
  if (!normalized) return []

  const { optionsById } = normalized

  const sortByLabel = <T extends { label: string }>(items: T[]): T[] =>
    [...items].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )

  const childrenByParent = new Map<string | null, string[]>()

  for (const option of Object.values(optionsById)) {
    const parentKey = option.optionOptionsId ?? null
    const existing = childrenByParent.get(parentKey)
    if (existing) {
      existing.push(option.id)
    } else {
      childrenByParent.set(parentKey, [option.id])
    }
  }

  const buildNode = (id: string): ViewOption | null => {
    const o = optionsById[id]
    if (!o) return null

    const childIds = childrenByParent.get(o.id) ?? []
    const children = childIds
      .map(buildNode)
      .filter((c): c is ViewOption => c !== null)
    const sortedChildren = children.length ? sortByLabel(children) : []

    const targetLang = lang ?? LEGACY_BASE_LANGUAGE
    const localized = o[targetLang]

    const translations: Partial<Record<Language, TranslationSchema>> = {}
    for (const lg of BUSINESS_LANGUAGES) {
      const t = o[lg]
      if (t) translations[lg] = t
    }

    return {
      id: o.id,
      label: localized?.label ?? o.label ?? '',
      value: o.value,
      isDefault: o.isDefault,
      status: o.status,
      updatedAt: o.updatedAt,
      fieldOptionsId: o.fieldOptionsId,
      optionOptionsId: o.optionOptionsId,
      metadata: o.metadata,
      userID: o.userID,
      organizationID: o.organizationID,
      options: sortedChildren.length ? sortedChildren : undefined,
      ...translations,
    }
  }

  const rootIds = childrenByParent.get(null) ?? []

  const roots = rootIds
    .map((id) => buildNode(id))
    .filter((c): c is ViewOption => c !== null)

  return sortByLabel(roots)
}

// Liste flat basée sur un arbre déjà traduit (ViewOption[])
export function buildFlatFromViewTree(options: ViewOption[]): ViewOption[] {
  if (!options.length) return []

  const result: ViewOption[] = []
  const stack: ViewOption[] = [...options]

  while (stack.length) {
    const current = stack.pop()
    if (!current) continue
    const { options: children, ...rest } = current

    result.push({
      ...rest,
      // On s'assure que les options "flat" n'ont pas d'enfants imbriqués
      options: undefined,
    })

    if (children && children.length > 0) {
      for (let index = 0; index < children.length; index += 1) {
        stack.push(children[index])
      }
    }
  }

  return result.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  )
}

/**
 * Minimal interface for options that can be transformed to SelectOption.
 * Both ViewOption and TranslatedOptionItem satisfy this interface.
 */
type SelectableOption = {
  id: string
  label: string
  value?: string | null
  isDefault?: boolean | null
  status?: string | null
  updatedAt?: string | null
  metadata?: { key: string; value?: string }[]
  userID?: string | null
  organizationID?: string | null
  options?: SelectableOption[]
}

/**
 * Transforms options into SelectOption[] for react-select consumption.
 * Works with both ViewOption and TranslatedOptionItem.
 *
 * @param options - Array of options (ViewOption or TranslatedOptionItem)
 * @param propertyKey - The property key (e.g., 'action') to determine value mapping
 * @returns SelectOption array ready for react-select
 */
export function toSelectOptions<T extends SelectableOption>(
  options: T[],
  propertyKey: string,
): SelectOption[] {
  return options.map((option): SelectOption => {
    const rawValue = option.value ?? ''

    const isInReview = option.status === ReviewStatus.PENDING
    const isDefault = hasDefaultOptionFlag(option)
    const icon = getMetadataValue(option.metadata, 'icon')
    const isQuickAction =
      getMetadataValue(option.metadata, 'isQuickAction') === 'true'

    const selectOption: SelectOption = {
      id: option.id,
      label: option.label,
      value: propertyKey === 'action' ? rawValue || option.id : option.id,
      isInReview,
      ...(isDefault ? { isDefault } : {}),
      status: option.status ?? undefined,
      ...(icon && { icon }),
      ...(isQuickAction && { isQuickAction }),
      userID: option.userID ?? undefined,
      organizationID: option.organizationID ?? undefined,
      metadata: option.metadata ?? [],
    }

    if (option.options && option.options.length > 0) {
      selectOption.options = toSelectOptions(option.options, propertyKey)
    }

    return selectOption
  })
}

export function addGroupToOptions<
  T extends { id: string; label: string; options?: readonly T[] },
>(
  items: readonly T[],
  rootSegments: readonly string[] = [],
  rootPath: readonly string[] = [],
): WithGroupRecursive<T>[] {
  const base = Array.isArray(rootSegments) ? rootSegments.filter(Boolean) : []
  const basePath = Array.isArray(rootPath) ? rootPath.filter(Boolean) : []
  return items.map((item) => {
    const currentPath = [...base, item?.label || ''].filter(Boolean)
    const currentTechnicalPath = [...basePath, item.id].filter(Boolean)
    const hasChildren = Array.isArray(item.options) && item.options.length > 0
    if (!hasChildren) {
      const { options: _omit, ...rest } = item
      return {
        ...rest,
        group: currentPath,
        path: currentTechnicalPath,
      }
    }
    const children = addGroupToOptions(
      item.options ?? [],
      currentPath,
      currentTechnicalPath,
    )
    const { options: _omit, ...rest } = item
    return {
      ...rest,
      group: currentPath,
      path: currentTechnicalPath,
      options: children,
    }
  })
}

export type WithGroup<T> = Omit<T, 'options'> & { group: string[] }

export function flattenWithGroup<
  T extends { group: string[]; options?: readonly T[] },
>(items: readonly T[]): Array<Omit<T, 'options'>> {
  const collected: Array<Omit<T, 'options'>> = []
  const stack: T[] = [...items]

  while (stack.length) {
    const current = stack.pop()
    if (!current) continue
    const { options: children, ...rest } = current
    collected.push(rest)
    if (children && children.length > 0) {
      for (let i = 0; i < children.length; i++) {
        stack.push(children[i])
      }
    }
  }

  const ordered: Array<Omit<T, 'options'>> = []
  for (let i = collected.length - 1; i >= 0; i -= 1) {
    ordered.push(collected[i])
  }

  return ordered
}

/**
 * Transforms ViewOption[] into OptionRow[] for AG Grid display.
 * Adds group path for tree data and flattens the hierarchy.
 */
export function buildRowsFromViewOptions(
  options: ViewOption[],
  supportedKey: SupportedKeys | undefined,
): OptionRow[] {
  const rootPathSegment =
    typeof supportedKey?.listId === 'string' && supportedKey.listId.length > 0
      ? supportedKey.listId
      : supportedKey?.id
  const withGroup = addGroupToOptions(
    options,
    supportedKey?.label ? [supportedKey.label] : [],
    rootPathSegment
      ? [rootPathSegment]
      : supportedKey?.label
        ? [supportedKey.label]
        : [],
  )
  const rows = flattenWithGroup(withGroup).map((row) => ({
    ...(row as OptionRow),
    normalizedLabel: normalizeOptionLabelForDiagnostics(row.label),
    parentGroupLabel:
      row.group.length > 2 ? row.group.slice(1, -1).join(' > ') : '',
  })) as OptionRow[]

  const duplicateCountByKey = new Map<string, number>()
  const rootScopeId = supportedKey?.listId ?? supportedKey?.id ?? 'root'

  rows.forEach((row) => {
    if (!row.normalizedLabel) return

    const duplicateScopeId =
      row.optionOptionsId ??
      row.fieldOptionsId ??
      row.path?.[row.path.length - 2] ??
      rootScopeId
    const duplicateKey = `${duplicateScopeId}::${row.normalizedLabel}`
    duplicateCountByKey.set(
      duplicateKey,
      (duplicateCountByKey.get(duplicateKey) ?? 0) + 1,
    )
  })

  const rowsWithDiagnostics = rows.map((row) => {
    if (!row.normalizedLabel) {
      return { ...row, duplicateCount: 0 }
    }

    const duplicateScopeId =
      row.optionOptionsId ??
      row.fieldOptionsId ??
      row.path?.[row.path.length - 2] ??
      rootScopeId
    const duplicateKey = `${duplicateScopeId}::${row.normalizedLabel}`

    return {
      ...row,
      duplicateCount: duplicateCountByKey.get(duplicateKey) ?? 1,
    }
  })

  return rowsWithDiagnostics.sort((a, b) =>
    a.group.join('>').localeCompare(b.group.join('>'), undefined, {
      sensitivity: 'base',
    }),
  )
}

/** Option input with required status for duplication */
type DuplicateOptionInput = OptionCreateInput & { status: string }

type DuplicateResult = {
  options: DuplicateOptionInput[]
  translations: TranslationInput[]
}

const isTranslationObject = (
  value: unknown,
): value is { id: string; label: string; lang: string } => {
  if (!value || typeof value !== 'object') return false
  const v = value as any
  return (
    typeof v.id === 'string' &&
    typeof v.label === 'string' &&
    typeof v.lang === 'string'
  )
}

export const duplicateOptionsWithTranslations = (params: {
  normalized: { optionsById: Record<string, NormalizedOption> }
  newFieldId: string
  targetStatus?: string
  targetUserID?: string
  targetOrganizationID?: string
}): DuplicateResult => {
  const {
    normalized,
    newFieldId,
    targetStatus,
    targetUserID,
    targetOrganizationID,
  } = params

  const idMap: Record<string, string> = {}
  const translations: TranslationInput[] = []

  // Map des nouveaux IDs
  Object.values(normalized.optionsById).forEach((option) => {
    idMap[option.id] = uuid()
  })

  const options: DuplicateOptionInput[] = Object.values(
    normalized.optionsById,
  ).map((option) => {
    const newId = idMap[option.id]
    const isRoot = option.optionOptionsId == null

    const newParentId =
      option.optionOptionsId != null
        ? (idMap[option.optionOptionsId] ?? null)
        : null

    const cleaned: DuplicateOptionInput = {
      id: newId,
      label: option.label ?? '',
      value: option.value ?? option.label ?? '',
      status: targetStatus ?? option.status ?? 'PENDING',
      metadata: option.metadata,
      userID: targetUserID ?? option.userID,
      organizationID: targetOrganizationID ?? option.organizationID,
      fieldOptionsId: isRoot ? newFieldId : undefined,
      optionOptionsId: isRoot ? undefined : (newParentId ?? undefined),
    }

    Object.entries(option).forEach(([_, value]) => {
      if (!isTranslationObject(value)) return
      translations.push({
        id: uuid(),
        optionTranslationsId: newId,
        lang: value.lang,
        label: value.label,
      })
    })

    return cleaned
  })

  return { options, translations }
}
